<!-- src/views/UsefulLinksView.vue -->
<template>
  <div class="p-4 sm:p-6 md:p-8">
    <div class="max-w-7xl mx-auto">

      <div class="flex items-center justify-between mb-8">
        <!-- UPDATED TITLE -->
        <h1 class="text-2xl sm:text-3xl font-bold text-gray-800">Useful Links</h1>
        <router-link to="/dashboard" class="text-sm text-brand-primary hover:text-brand-accent hover:underline">
          ← Back to Main Dashboard
        </router-link>
      </div>

      <!-- UPDATED LOADING TEXT -->
      <div v-if="loading" class="text-center py-10">Loading useful links...</div>
      <div v-else-if="error" class="text-red-500 text-center py-10">{{ error }}</div>
      
      <!-- UPDATED EMPTY STATE TEXT -->
      <div v-else-if="visibleResources.length === 0" class="text-center py-10 bg-white p-6 rounded-lg shadow-md">
          <p class="text-gray-600">No useful links have been configured by your administrator yet.</p>
      </div>

      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div 
          v-for="(resource, index) in visibleResources" 
          :key="index" 
          class="resource-card group"
        >
          <div class="card-image-wrapper">
            <img v-if="resource.imageUrl" :src="resource.imageUrl" :alt="resource.title" class="card-image">
            <div v-else class="card-placeholder-icon">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            </div>
          </div>
          <div class="p-4 flex flex-col flex-grow">
            <h3 class="card-title">{{ resource.title }}</h3>
            <div class="mt-auto pt-4 space-y-2">
                
                <!-- Special case for 'Other' to link to ComingSoonView -->
                <router-link
                  v-if="resource.title === 'Other'"
                  :to="{ name: 'engineeringProjects' }"
                  class="card-link-button"
                >
                  <span>Open Link</span>
                  <span>&rarr;</span>
                </router-link>

                <!-- Default URL Link for all other resources -->
                <a v-else-if="resource.url" :href="resource.url" target="_blank" rel="noopener noreferrer" class="card-link-button">
                    <span>Open Link</span>
                    <span>&rarr;</span>
                </a>

                <!-- Document Link (can coexist with a URL link) -->
                <a v-if="resource.documentUrl" :href="resource.documentUrl" target="_blank" rel="noopener noreferrer" class="document-link" :title="resource.documentName">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                    <span class="truncate">{{ resource.documentName || 'Download File' }}</span>
                </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { db, auth } from '@/firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

const loading = ref(true);
const error = ref(null);
const resources = ref([]);

const visibleResources = computed(() => {
    // Logic remains the same: show if it has a URL, a Document, or is the special "Other" tile
    return resources.value.filter(r => r && r.title && ((r.url || r.documentUrl) || r.title === 'Other'));
});

// We still fetch from the 'resources' field in the database, as that is the backend data structure
const fetchResources = async (clientId) => {
    try {
        const clientDocRef = doc(db, 'clients', clientId);
        const docSnap = await getDoc(clientDocRef);
        if (docSnap.exists() && docSnap.data().resources) {
            resources.value = docSnap.data().resources;
        }
    } catch (err) {
        error.value = "Failed to load useful links.";
        console.error(err);
    } finally {
        loading.value = false;
    }
};

onMounted(() => {
  onAuthStateChanged(auth, async (user) => {
    if (user) {
      try {
        const idTokenResult = await user.getIdTokenResult();
        const clientId = idTokenResult.claims.clientId;
        if (clientId) {
          await fetchResources(clientId);
        } else {
          error.value = "Your account is not associated with a client.";
          loading.value = false;
        }
      } catch (err) {
        error.value = "Could not verify your client association.";
        loading.value = false;
      }
    } else {
      // UPDATED ERROR TEXT
      error.value = "You must be logged in to view useful links.";
      loading.value = false;
    }
  });
});
</script>

<style scoped>
.resource-card {
  @apply flex flex-col bg-white rounded-lg shadow-md overflow-hidden transition-all duration-300 ease-in-out;
  @apply hover:shadow-xl hover:-translate-y-1 border border-gray-200;
}
.card-image-wrapper {
  @apply relative w-full bg-gray-100;
  padding-top: 100%;
}
.card-image, .card-placeholder-icon {
  @apply absolute top-0 left-0 w-full h-full object-cover;
}
.card-placeholder-icon {
    @apply flex items-center justify-center bg-gray-50;
}
.card-title {
  @apply text-lg font-bold text-gray-800;
}
.card-link-button {
  @apply w-full flex justify-between items-center text-sm font-semibold text-brand-primary p-2 rounded-md transition-colors bg-gray-50 hover:bg-gray-100 group-hover:text-brand-accent;
}
.document-link {
    @apply w-full flex items-center text-xs text-gray-600 font-medium p-2 rounded-md transition-colors bg-gray-50 hover:bg-gray-100 group-hover:text-brand-primary;
}
</style>